import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, from, map, Observable, of } from "rxjs";
import { IDBPDatabase, openDB } from "idb";
import { switchMap } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class ReferenceCacheService {

  private dbSubject: BehaviorSubject<IDBPDatabase<any> | null>;

  constructor() {
    this.dbSubject = new BehaviorSubject<IDBPDatabase<any> | null>(null);

    openDB('reference-data-cache-db', 1, {
      upgrade(db) {
        db.createObjectStore('reference-data', { keyPath: 'id' });
      },
    }).then(db => {
      this.dbSubject.next(db);
    }).catch(error => {
      console.error('Failed to open IndexedDB:', error);
    });
  }

  private getDatabase(): Observable<IDBPDatabase<any>> {
    return this.dbSubject.asObservable().pipe(
      filter((db): db is IDBPDatabase<any> => !!db)
    );
  }

  // Кэширование справочников
  cacheReferenceData(id: string, data: any): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.put('reference-data', { id, data, timestamp: Date.now() })))
    );
  }

  // Получение справочников из кэша
  getCachedReferenceData(id: string): Observable<any | null> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.get('reference-data', id))),
      switchMap(result => of(result ? result.data : null))
    );
  }

  // Метод для получения справочников (из кэша или API)
  getReferenceData(id: string, loadFromAPI: () => Observable<any>): Observable<any> {
    return this.getCachedReferenceData(id).pipe(
      switchMap((cachedData) => {
        if (cachedData) {
          return of(cachedData); // Вернуть кэшированные данные, если они есть
        } else {
          return loadFromAPI().pipe( // Если данных нет, загружаем через API
            switchMap((data) =>
              this.cacheReferenceData(id, data).pipe( // Кэшируем загруженные данные
                map(() => data)
              )
            )
          );
        }
      }),
      catchError((error) => {
        console.error('Ошибка при загрузке справочников:', error);
        return of([]);
      })
    );
  }

  // Удаление справочников из кэша по ID
  deleteReferenceData(id: string): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.delete('reference-data', id)))
    );
  }

  // Очистка всего кэша справочников
  clearCache(): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.clear('reference-data')))
    );
  }

  // Удаление старых данных
  deleteOldReferenceData(): Observable<void> {
    const expirationDate = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000); // Данные старше 5 дней

    return this.getDatabase().pipe(
      switchMap(db => {
        const transaction = db.transaction('reference-data', 'readwrite');
        const store = transaction.objectStore('reference-data');
        const request = store.openCursor();

        return from(request).pipe(
          switchMap(function processCursor(cursor): Observable<void> {
            if (!cursor) return of(undefined) as Observable<void>;

            const record = cursor.value;
            const recordDate = new Date(record.timestamp);

            let delete$ = of(undefined) as Observable<void>;
            if (recordDate < expirationDate) {
              delete$ = from(cursor.delete()).pipe(
                catchError(err => {
                  console.error('Failed to delete record:', err);
                  return of(undefined) as Observable<void>;
                })
              );
            }

            return delete$.pipe(
              switchMap(() => from(cursor.continue())),
              switchMap(cursor => processCursor(cursor))
            );
          }),
          catchError((error) => {
            console.error('Failed to delete old reference data:', error);
            return of(undefined) as Observable<void>;
          })
        );
      })
    );
  }

  // Запуск очистки старых данных (ежедневная проверка)
  runDailyCleanup(): void {
    const lastCleanup = localStorage.getItem('lastReferenceDataCleanup');
    const now = Date.now();

    if (!lastCleanup || now - parseInt(lastCleanup, 10) > 24 * 60 * 60 * 1000) {
      this.deleteOldReferenceData().subscribe({
        next: () => {
          localStorage.setItem('lastReferenceDataCleanup', now.toString());
        },
        error: (error) => {
          console.error('Error while deleting old reference data:', error);
        }
      });
    }
  }
}
